import { useState, useEffect, useRef } from 'react';

/**
 * useVendorNames — löst SAP-Lieferantennummern (LIFNR) über die Live-Wertehilfe
 * `/api/value-help/vendor` in Klartextnamen auf (debounced, mit Cache je Hook-Instanz).
 * Verwendung: Name dezent unter Lieferanten-Eingabefeldern anzeigen (Bezugsquelle-Tab,
 * BANF-Allgemein-Tab, …).
 *
 * @param {Array<string>} codes  aktuelle Feldwerte (nur numerische LIFNR werden nachgeschlagen)
 * @param {string} token         JWT
 * @returns {(value:string)=>string|null}  Lookup: LIFNR -> Name (null solange unbekannt)
 */
export default function useVendorNames(codes, token) {
  const [names, setNames] = useState({});
  const timer = useRef(null);

  const wanted = (codes || [])
    .map((v) => String(v || '').trim())
    .filter((v) => /^\d{4,}$/.test(v));
  const wantedKey = wanted.join('|');

  useEffect(() => {
    const missing = wanted.filter((v) => names[v] === undefined);
    if (missing.length === 0 || !token) return undefined;

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      for (const code of missing) {
        try {
          const res = await fetch(`/api/value-help/vendor?q=${encodeURIComponent(code)}&limit=3`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = res.ok ? await res.json() : null;
          const rows = (data && data.rows) || [];
          // Treffer nur übernehmen, wenn die Nummer wirklich passt (LFA1 intern mit führenden Nullen).
          const hit = rows.find((r) => String(r.code).replace(/^0+/, '') === code.replace(/^0+/, ''));
          setNames((prev) => ({ ...prev, [code]: hit ? hit.text : null }));
        } catch {
          setNames((prev) => ({ ...prev, [code]: null }));
        }
      }
    }, 400);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // names bewusst nicht in den Deps (Cache-Schreiben würde sonst re-triggern).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey, token]);

  return (value) => {
    const code = String(value || '').trim();
    return code ? names[code] || null : null;
  };
}
